import { useRef, useImperativeHandle, forwardRef, useState } from "react";
import { Box, Typography, IconButton, Slider, useTheme, alpha, Tooltip, Fade } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import ReplayIcon from "@mui/icons-material/Replay";
import Forward10Icon from "@mui/icons-material/Forward10";
import Replay10Icon from "@mui/icons-material/Replay10";

const DocVideoPlayer = forwardRef(({ videoUrl, title }, ref) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const videoElRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentStepLabel, setCurrentStepLabel] = useState("");
  const [showControls, setShowControls] = useState(true);
  const [isEnded, setIsEnded] = useState(false);
  const hideControlsTimer = useRef(null);

  useImperativeHandle(ref, () => ({
    seekTo: (seconds, label = "") => {
      if (videoElRef.current) {
        videoElRef.current.currentTime = seconds;
        videoElRef.current.play();
        setIsPlaying(true);
        setIsEnded(false);
        setCurrentStepLabel(label);
        flashControls();
        // Clear label after 3s
        setTimeout(() => setCurrentStepLabel(""), 3000);
      }
    },
  }));

  const flashControls = () => {
    setShowControls(true);
    clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = setTimeout(() => setShowControls(false), 3000);
  };

  const handleMouseMove = () => flashControls();

  const handlePlayPause = () => {
    if (!videoElRef.current) return;
    if (isEnded) {
      videoElRef.current.currentTime = 0;
      setIsEnded(false);
    }
    if (isPlaying) {
      videoElRef.current.pause();
    } else {
      videoElRef.current.play();
    }
    setIsPlaying(!isPlaying);
    flashControls();
  };

  const handleMute = () => {
    if (!videoElRef.current) return;
    videoElRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    if (videoElRef.current) setCurrentTime(videoElRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (videoElRef.current) setDuration(videoElRef.current.duration);
  };

  const handleSliderChange = (_, value) => {
    if (videoElRef.current) {
      videoElRef.current.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleFullscreen = () => {
    if (videoElRef.current) videoElRef.current.requestFullscreen();
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setIsEnded(true);
    setShowControls(true);
    setCurrentStepLabel("");
  };

  const handleSkip = (seconds) => {
    if (videoElRef.current) {
      videoElRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
      flashControls();
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        border: `1px solid ${isDark ? alpha("#fff", 0.08) : alpha("#000", 0.1)}`,
        boxShadow: isDark
          ? `0 4px 32px ${alpha("#000", 0.5)}`
          : `0 4px 24px ${alpha("#000", 0.12)}`,
        position: "relative",
        backgroundColor: "#000",
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      {/* Video */}
      <Box sx={{ position: "relative", lineHeight: 0 }}>
        <video
          ref={videoElRef}
          src={videoUrl}
          style={{
            width: "100%",
            maxHeight: 440,
            display: "block",
            objectFit: "contain",
            cursor: showControls ? "default" : "none",
          }}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={handleEnded}
          onClick={handlePlayPause}
        />

        {/* Center play/pause click target overlay */}
        <Box
          onClick={handlePlayPause}
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {/* Big play button shown when paused/ended */}
          <Fade in={!isPlaying}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: alpha("#000", 0.55),
                backdropFilter: "blur(8px)",
                border: `1.5px solid ${alpha("#fff", 0.25)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "transform 0.15s ease",
                "&:hover": { transform: "scale(1.1)" },
              }}
            >
              {isEnded ? (
                <ReplayIcon sx={{ color: "#fff", fontSize: 28 }} />
              ) : (
                <PlayArrowIcon sx={{ color: "#fff", fontSize: 32, ml: "3px" }} />
              )}
            </Box>
          </Fade>
        </Box>

        {/* Step label pill */}
        <Fade in={!!currentStepLabel}>
          <Box
            sx={{
              position: "absolute",
              top: 14,
              left: 14,
              backgroundColor: "rgba(58,142,255,0.9)",
              backdropFilter: "blur(8px)",
              color: "#fff",
              px: 1.5,
              py: 0.5,
              borderRadius: 1.5,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: "0.02em",
              boxShadow: "0 2px 12px rgba(58,142,255,0.4)",
              pointerEvents: "none",
            }}
          >
            ▶ {currentStepLabel}
          </Box>
        </Fade>
      </Box>

      {/* Controls bar */}
      <Fade in={showControls}>
        <Box
          sx={{
            background: isDark
              ? `linear-gradient(to top, ${alpha("#000", 0.95)}, ${alpha("#0a0a0a", 0.85)})`
              : `linear-gradient(to top, ${alpha("#000", 0.92)}, ${alpha("#111", 0.8)})`,
            px: 2,
            pt: 1,
            pb: 1.5,
          }}
        >
          {/* Progress slider */}
          <Slider
            size="small"
            value={currentTime}
            min={0}
            max={duration || 100}
            step={0.1}
            onChange={handleSliderChange}
            sx={{
              p: 0,
              mb: 0.75,
              height: 3,
              color: "#3a8eff",
              "& .MuiSlider-thumb": {
                width: 12,
                height: 12,
                opacity: 0,
                transition: "opacity 0.15s",
                "&:hover, &.Mui-focusVisible": { opacity: 1, boxShadow: "0 0 0 6px rgba(58,142,255,0.2)" },
              },
              "&:hover .MuiSlider-thumb": { opacity: 1 },
              "& .MuiSlider-track": {
                height: 3,
                border: "none",
                background: "linear-gradient(to right, #3a8eff, rgba(96,165,250,0.9))",
              },
              "& .MuiSlider-rail": { height: 3, backgroundColor: alpha("#fff", 0.2) },
            }}
          />

          {/* Buttons row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title={isPlaying ? "Pause" : "Play"}>
              <IconButton onClick={handlePlayPause} size="small" sx={{ color: "#fff" }}>
                {isPlaying ? <PauseIcon sx={{ fontSize: 20 }} /> : <PlayArrowIcon sx={{ fontSize: 20 }} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Rewind 10s">
              <IconButton onClick={() => handleSkip(-10)} size="small" sx={{ color: alpha("#fff", 0.7) }}>
                <Replay10Icon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Forward 10s">
              <IconButton onClick={() => handleSkip(10)} size="small" sx={{ color: alpha("#fff", 0.7) }}>
                <Forward10Icon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Time */}
            <Typography
              sx={{
                color: alpha("#fff", 0.7),
                fontSize: 12,
                fontFamily: "monospace",
                letterSpacing: "0.05em",
                mx: 0.5,
                userSelect: "none",
              }}
            >
              {formatTime(currentTime)}{" "}
              <span style={{ color: alpha("#fff", 0.35) }}>/ {formatTime(duration)}</span>
            </Typography>

            {/* Step label in bar */}
            {currentStepLabel && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: "rgba(96,165,250,0.9)",
                  ml: 0.5,
                  fontWeight: 500,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {currentStepLabel}
              </Typography>
            )}

            <Box sx={{ flex: 1 }} />

            <Tooltip title={isMuted ? "Unmute" : "Mute"}>
              <IconButton onClick={handleMute} size="small" sx={{ color: alpha("#fff", 0.7) }}>
                {isMuted ? <VolumeOffIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>

            <Tooltip title="Fullscreen">
              <IconButton onClick={handleFullscreen} size="small" sx={{ color: alpha("#fff", 0.7) }}>
                <FullscreenIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
});

DocVideoPlayer.displayName = "DocVideoPlayer";
export default DocVideoPlayer;
